import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import store from "../../../../State/Store/store";
import {
  openErrorPopup,
  setUserInterfaceDisabled,
  stopAutoPlay,
} from "../../../../State/actions";
import {
  selectErrorPopupActive,
  selectCriticalAnimationPlaying,
  selectAutoPlayActive,
  selectIsSpinning,
  selectAnyFeatureTriggeredOrActive,
} from "../../../../State/selectors";
import {
  SESSION_TIMEOUT_CODE,
  getIdleLogoutMs,
  idleLogoutDecision,
} from "../../../../utils/sessionTimeout";
import { baseDiag } from "../../../../utils/fsDiag";

/**
 * Ends the session after the operator's inactivity interval — GDD §14/§15.
 *
 * Renders nothing. Mounted once, beside HeartbeatManager.
 *
 * ── What counts as ACTIVITY ──
 *
 * Player input — a tap, a click, a key — AND the game actually playing:
 * autoplay running, a spin in flight, or a feature on screen.
 *
 * ── Autoplay is NOT idle. Corrected 2026-09-10 ──
 *
 * This used to be input-only, on the reasoning that a player who starts a
 * hundred autospins and walks away is exactly the session an operator wants
 * closed. In practice it fired mid-autoplay on a live board with money moving,
 * which Vladimir rejected outright: "if the game is running and playing I'm not
 * inactive."
 *
 * He is right, and the input-only reading was too literal. The rule is about a
 * session left DORMANT, not one mid-round. A logout that lands while the reels
 * are turning also has to tear down a spin it does not own — the popup already
 * had to call stopAutoPlay for exactly that reason, which was the clue.
 *
 * So the clock does not merely PAUSE while the game plays, it is reset each
 * tick. The twenty minutes start from the moment the game goes quiet, not from
 * the last touch before a long autoplay run — otherwise a session would end the
 * instant autoplay finished, which is the same bug wearing a hat.
 *
 * `pointerdown` covers mouse and touch in one event, so there is no separate
 * touch listener to keep in step. All three are passive and on `window`, the
 * same shape useRevealSkip uses — the canvas swallows nothing, so a tap on the
 * game reaches here too.
 *
 * ── Why it polls instead of arming one long timer ──
 *
 * A single `setTimeout(interval)` re-armed on every input would restart a
 * twenty-minute timer on each of hundreds of taps, which is fine, but it also
 * keeps no record of WHEN the session actually went quiet — so a tab that was
 * backgrounded and throttled resumes with a timer that has drifted. A
 * timestamp plus a cheap tick compares real clock time and cannot drift.
 *
 * The tick is 10s at the real interval: twenty minutes is not a deadline that
 * needs sub-second accuracy, and this way the whole feature costs one
 * comparison every ten seconds.
 *
 * It scales DOWN for a short one, though — a 15s test interval polled every 10s
 * fires somewhere between 15 and 25 seconds and looks broken. A quarter of the
 * interval, capped at 10s, keeps the overshoot proportional at any setting.
 *
 * ── It waits for a quiet moment ──
 *
 * Firing mid-feature would tear down the UI over a running bonus. When the
 * limit is reached during a critical animation the check simply comes back on
 * the next tick — the player is not there, so a few more seconds costs nothing,
 * and the alternative is a popup over a half-finished feature.
 */
const IdleLogoutManager = () => {
  const dispatch = useDispatch();
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);

  const lastActivityRef = useRef(Date.now());
  const firedRef = useRef(false);

  // Read once. The operator's rule cannot change mid-session, and re-reading
  // it would let a late-loading options object silently arm the feature.
  const intervalMsRef = useRef(getIdleLogoutMs());

  // Held in refs so the listeners below are attached ONCE. Reading these from
  // the render scope would rebuild the listener set on every popup or feature
  // state change, and an input landing in that gap would not reset the clock.
  const popupActiveRef = useRef(errorPopupActive);
  popupActiveRef.current = errorPopupActive;
  const criticalRef = useRef(criticalAnimationPlaying);
  criticalRef.current = criticalAnimationPlaying;

  // "The game is playing." Same ref treatment as the two above, and for the
  // same reason — the listeners and the interval are attached once.
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const isSpinning = useSelector(selectIsSpinning);
  const featureActive = useSelector(selectAnyFeatureTriggeredOrActive);
  const playingRef = useRef(false);
  playingRef.current = autoPlayActive || isSpinning || featureActive;
  // Named individually so the stuck-flag line can say which one is holding.
  const autoPlayRef = useRef(autoPlayActive);
  autoPlayRef.current = autoPlayActive;
  const spinningRef = useRef(isSpinning);
  spinningRef.current = isSpinning;
  const featureRef = useRef(featureActive);
  featureRef.current = featureActive;
  // One line per session, not one per tick.
  const stuckLoggedRef = useRef(false);

  useEffect(() => {
    const intervalMs = intervalMsRef.current;
    // No rule from the operator — nothing attached, nothing polled. This is
    // also what keeps the feature inert for every provider that does not send
    // `license_rules`.
    if (!intervalMs) return undefined;

    const onActivity = () => {
      // Once the session has ended, input must not revive it. The only way
      // back is the popup, which reloads.
      if (firedRef.current) return;
      lastActivityRef.current = Date.now();
      // Genuine activity — if it was ever held open, it is not now.
      stuckLoggedRef.current = false;
    };

    window.addEventListener("pointerdown", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity, { passive: true });

    // ── A SESSION THAT IS TALKING TO THE SERVER IS NOT IDLE ──
    //
    // Vladimir, 2026-09-10: "it doesn't matter if the user hasn't pressed
    // anything, requests are being sent constantly."
    //
    // The three state flags above describe the game PRESENTING. This describes
    // it TRANSACTING, and it is the stronger signal of the two: rgsPlayInFlight
    // is raised by rgsPlay.pending and by nothing else, so every base spin,
    // free spin, gamble answer and bonus buy passes through it. Anything the
    // flags might not name still resets the clock here.
    //
    // Subscribed to the store rather than selected, so the reset lands on the
    // dispatch itself — a render later would be a render of drift on a clock
    // measured in minutes, which is harmless, but there is no reason to add it.
    let wasInFlight = store.getState().spin?.rgsPlayInFlight === true;
    const unsubscribeRequests = store.subscribe(() => {
      const inFlight = store.getState().spin?.rgsPlayInFlight === true;
      // Rising edge only. Holding the clock while a request is OUT would let a
      // hung request keep a dead session alive for ever.
      if (inFlight && !wasInFlight) onActivity();
      wasInFlight = inFlight;
    });

    const tick = setInterval(() => {
      // ── The decision is NOT made here ──
      //
      // idleLogoutDecision owns it, so the rule can be tested without a DOM, a
      // store or a clock — see utils/__tests__/sessionTimeout.test.js, which
      // pins both failure directions. This function owns only the listeners,
      // the polling and the teardown.
      const now = Date.now();
      const { end, resetClock, stuck } = idleLogoutDecision({
        now,
        lastActivityAt: lastActivityRef.current,
        intervalMs,
        playing: playingRef.current,
        popupActive: popupActiveRef.current,
        criticalActive: criticalRef.current,
        alreadyFired: firedRef.current,
      });

      if (stuck && !stuckLoggedRef.current) {
        // ── Said once, and never acted on ──
        //
        // Every signal in this rule is "keep alive", so a flag stuck true does
        // not break loudly — it silently switches the operator's logout off. A
        // session ending early annoys a player; one that never ends fails an
        // audit, quietly, months later. Two flags stuck exactly this way were
        // found and fixed on 2026-09-10 (freeSpins.active surviving a replay,
        // rgsPlayInFlight never cleared after a replay step), so this is a
        // known shape, not a hypothetical.
        //
        // Logged rather than enforced: ending a session because a flag LOOKS
        // stuck would resurrect the very bug this change removed.
        stuckLoggedRef.current = true;
        baseDiag("idle logout HELD OPEN past the limit — check for a stuck flag", {
          heldForMs: now - lastActivityRef.current,
          intervalMs,
          autoPlay: autoPlayRef.current,
          spinning: spinningRef.current,
          feature: featureRef.current,
        });
      }

      if (resetClock) {
        lastActivityRef.current = now;
        return;
      }
      if (!end) return;

      firedRef.current = true;
      // Autoplay first: it is the thing most likely to still be running, and
      // it must not queue another spin behind the popup.
      dispatch(stopAutoPlay());
      // WarningPopup disables the UI itself when it opens, so this is one
      // render early rather than strictly required — and that render is a frame
      // of live board on a session the operator has just closed.
      dispatch(setUserInterfaceDisabled(true));
      dispatch(openErrorPopup(SESSION_TIMEOUT_CODE));
      // A quarter of the limit, never slower than 10s. At 20 minutes that is
      // the 10s tick; at a 15s test interval it is under 4s, so the popup
      // arrives when you expect it to instead of up to 10s late.
    }, Math.min(10000, Math.max(250, intervalMs / 4)));

    return () => {
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
      unsubscribeRequests();
      clearInterval(tick);
    };
  }, [dispatch]);

  return null;
};

export default React.memo(IdleLogoutManager);
